import { useEffect, useRef } from 'react';
import { useDeviceType } from '@vfit/shared/hooks';
import { getTemplateData, volaModalManager } from '@vfit/shared/data-access';
import { SlideProduct } from '@vfit/consumer/components';
import {
  Button,
  ButtonSlideCTA,
  ContainerTopProduct,
  ImageDivider,
  CustomText,
} from '@vfit/shared/atoms';
import { ICmsConfigAutoDesc, useAutoDesc, useRedirectCoverageToolFlow } from '@vfit/consumer/hooks';
import { Fade } from 'react-awesome-reveal';
import {
  checkIsApp,
  checkPanicMode,
  CONSUMER_CMS_DEFAULT,
  getClickToCallUrlByActionType,
  getRedirectCoverage,
  IGlobalConfigCMS,
  IGlobalPromoCMS,
  IN_ACTIVATION,
  IProduct,
  IWinBackCampaignConfig,
  IWinBackCMS,
  redirectToShoppingCartFromReverseAddress,
  trackActionByActionType,
  useCmsConfig,
  usePage,
} from '@vfit/consumer/data-access';
import { fonts } from '@vfit/shared/themes';
import {
  ActionTextType,
  IActionSlide,
  IActionType,
  IClickToCallMeData,
  IErrorFormApi,
  ISubmitOutput,
  IType,
} from '@vfit/shared/models';
import {
  API,
  getButtonActionByActionType,
  IGetAllCmsForm,
  manageErrorLeadPlatform,
  manageSuccessLeadPlatform,
  openLeadModal,
  openPopup,
  trackLink,
} from '@vfit/shared/data-access';
import { useQueryClient } from 'react-query';
import { SoftManagerService } from '@vfit/consumer/providers';
import { useRouter } from 'next/router';
import { ButtonDesc, DisclaimerContainer, DescriptionContainer } from '../../topProduct.style';
import { ITopProductProps } from '../../topProduct.models';
import { checkProductOfferFlow } from '../../topProduct.utils';
import { ProductCharacteristics } from '../ProductCharacteristics/productCharacteristics';

const TopNavigation = ({
  product: topProduct,
  disableNavigation,
  disclaimer,
  divider,
  isR02,
  onOverrideCoverageToolCta,
  isActiveAddress,
  addressAndTechnology,
  onSetModal,
  isShowCoverageTool,
  onShowCoverageTool,
}: ITopProductProps) => {
  const { push } = useRouter();
  const { cmsId } = topProduct;
  const queryClient = useQueryClient();
  const { page } = usePage();
  const winbackCampaignsList = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_WINBACK_CONFIG
  ) as IWinBackCampaignConfig;
  const cmsWinbackData = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_WINBACK_DATA
  ) as IWinBackCMS;
  const { globalconfig } =
    (useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_GLOBAL_CONFIG) as IGlobalConfigCMS) || {};
  const { globalpromo } = checkIsApp()
    ? (useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_GLOBAL_PROMO_IADD) as IGlobalPromoCMS) || {}
    : (useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_GLOBAL_PROMO) as IGlobalPromoCMS) || {};
  const { panicMode } = globalconfig || {};
  const volaClickToCallData = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_CLICK_TO_CALL_CONF
  ) as IClickToCallMeData;
  const ctcAutoDesc = useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_CTC_AUTO_DESC);
  const { enableAutoDesc, customString } =
    (ctcAutoDesc?.['ctcautodesc'] as ICmsConfigAutoDesc) || {};
  const product: IProduct = checkProductOfferFlow(topProduct, winbackCampaignsList, cmsWinbackData);
  const baseCtcUrl =
    product?.ctcPdpUrl || product?.pdpSecondAction?.callMeNow || product?.pdpSecondAction?.url;
  const iadd_auto_ctc = useAutoDesc(product as IProduct, {
    enableAutoDesc,
    url: baseCtcUrl,
    customString,
  });

  const { isMobile } = useDeviceType();
  const [isInActivation] = useRedirectCoverageToolFlow(cmsId || '');
  const productContentRef = useRef<HTMLDivElement | null>(null);
  const slideProductMainBtn = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isShowCoverageTool && isInActivation) {
      onShowCoverageTool?.(true);
    }
  }, [isInActivation]);

  const productDetailButton = (): JSX.Element | null => {
    if (!product.offerDetailLabel || (isMobile && !checkIsApp())) return null;
    const showDetail = () => {
      trackLink('product details', 'users');
      onSetModal?.({
        show: true,
        disableOrganizeText: false,
        type: IType.PRODUCT,
      });
    };
    return (
      <ButtonDesc>
        <ButtonSlideCTA isApp={checkIsApp()}>
          <Button name="action_showDetail" onClick={showDetail} variant={'secondary'}>
            {product.offerDetailLabel}
          </Button>
        </ButtonSlideCTA>
      </ButtonDesc>
    );
  };

  const productLandingClubButton = (): JSX.Element | null => {
    if (!product.isLandingClub || (isMobile && !checkIsApp())) return null;
    if (product?.hubSecondAction?.title)
      return (
        <ButtonDesc>
          <ButtonSlideCTA isApp={checkIsApp()}>
            <Button
              variant={'secondary'}
              name="action_showDetail"
              onClick={() =>
                getButtonActionByActionType(product?.hubSecondAction, push, queryClient)
              }
            >
              {product.hubSecondAction?.title}
            </Button>
          </ButtonSlideCTA>
        </ButtonDesc>
      );
    return null;
  };

  const onClickTypeInText = (
    type: ActionTextType,
    description: string | undefined,
    title?: string
  ) => {
    if (description) {
      onSetModal?.({
        show: true,
        type: IType.TEXT,
        text: description || '',
        title: title || '',
        disableOrganizeText: true,
      });
    }
  };

  const scrollToContent = () => {
    if (productContentRef?.current) {
      productContentRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const onActionProduct = (action?: IActionSlide) => {
    let allLeads;
    let refLead;
    let lead;
    let autoActionUrlOverride;
    let finalAction;
    let pstEnrgEvent;
    trackActionByActionType({
      type: action?.type,
      title: action?.title,
    });
    switch (action?.type) {
      case IActionType.CUSTOM_URL:
        if (isR02?.() && product?.redirectR02) {
          window.open(product.redirectR02 || '', action?.isBlank === 'true' ? '_blank' : '_self');
        } else if (action?.url) {
          window.open(action.url, action?.isBlank === 'true' ? '_blank' : '_self');
        }
        break;
      case IActionType.CALL_ME_NOW:
        autoActionUrlOverride = (iadd_auto_ctc?.ctcAutoUrl ||
          product?.ctcPdpUrl ||
          action?.callMeNow) as string;
        finalAction = checkIsApp()
          ? autoActionUrlOverride
          : product?.ctcPdpUrl || action?.callMeNow;
        if (finalAction) openPopup(finalAction);
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (action?.title && action?.url) {
          if (action?.template && action?.template !== '') {
            const templateData = getTemplateData(action?.template, queryClient);
            volaModalManager.handleVolaModal({
              url: getClickToCallUrlByActionType(action, product) || '',
              cmsClickToCallData: { ...templateData },
              taggingOption: page?.params?.elements?.taggingOptions,
              pageProduct: SoftManagerService(queryClient).getPageProduct(product),
            });
          } else {
            volaModalManager.handleVolaModal({
              url: getClickToCallUrlByActionType(action, product) || '',
              cmsClickToCallData: { ...volaClickToCallData },
              taggingOption: page?.params?.elements?.taggingOptions,
              pageProduct: SoftManagerService(queryClient).getPageProduct(product),
            });
          }
        }
        break;
      case IActionType.COVERAGE_TOOL:
        if (onOverrideCoverageToolCta) onOverrideCoverageToolCta();
        else onShowCoverageTool?.(true);
        break;
      case IActionType.AUTO_COVERAGE:
        if (cmsId) redirectToShoppingCartFromReverseAddress(cmsId);
        break;
      case IActionType.CHECKOUT_MODAL_MOBILE:
        localStorage.setItem('tProd', JSON.stringify(product));
        window.location.href = getRedirectCoverage();
        break;
      case IActionType.COVERAGE_TOOL_WITH_REDIRECT:
        if (action?.url && cmsId) {
          const alreadyHaveParams = action.url.includes('?');
          const redirectUrl = alreadyHaveParams
            ? `${action.url}&${IN_ACTIVATION}=${cmsId}`
            : `${action.url}?${IN_ACTIVATION}=${cmsId}`;
          window.location.href = redirectUrl;
        }
        break;
      case IActionType.LEAD_MODAL:
        allLeads = queryClient?.getQueryData('getAllLeads') as IGetAllCmsForm;
        // form name
        refLead = action?.localUrl || '';
        lead = allLeads[refLead];
        if (lead?.forms) {
          openLeadModal({
            form: lead.forms,
            onSuccess: (submitOutput: ISubmitOutput) =>
              manageSuccessLeadPlatform(submitOutput, push, queryClient),
            onError: (submitOutput: ISubmitOutput[], errorCmsApi?: IErrorFormApi) =>
              manageErrorLeadPlatform(submitOutput, errorCmsApi, push, queryClient),
          });
        }
        break;
      case IActionType.POST_ENRG:
        pstEnrgEvent = new CustomEvent('enrgServiceNotificationEvent', {
          detail: {
            nof: action?.localUrl,
          },
        });
        document.dispatchEvent(pstEnrgEvent);
        break;
      default:
        break;
    }
  };

  const getProductElements = () => {
    const productCharacteristics = product?.offerLockInApp?.shortCharacteristicsLockIn?.items || [];
    const elements = productCharacteristics.map((p) => ({
      text: p?.title || '',
      subtext: p?.description || '',
      icon: p?.icon || '',
    }));
    if (product?.isGlobalPromoActive) {
      if (globalpromo?.label) {
        elements.unshift({
          text: '',
          subtext: globalpromo.label || '',
          icon: 'promo.png',
        });
      }
    }
    return elements;
  };

  return (
    <>
      <SlideProduct
        ref={slideProductMainBtn}
        disableNavigation={disableNavigation}
        onActiveProduct={onActionProduct}
        product={product}
        globalPromo={globalpromo}
        onButtonScroll={scrollToContent}
        handleClickInflation={(inflationHtml) => {
          if (inflationHtml) onClickTypeInText(ActionTextType.club, inflationHtml);
        }}
        isPanicMode={checkPanicMode(panicMode, product)}
        isActiveAddress={isActiveAddress}
        addressAndTechnology={addressAndTechnology}
      />
      {product.productDescription && !isActiveAddress && (
        <>
          <ContainerTopProduct ref={productContentRef}>
            <Fade direction="up" triggerOnce>
              <DescriptionContainer>
                {!checkIsApp() &&
                  product.isGlobalPromoActive &&
                  globalpromo?.detailPageDescription && (
                    <CustomText
                      text={globalpromo.detailPageDescription}
                      textAlign="left"
                      onClickType={onClickTypeInText}
                      size={52}
                      lineHeight={70}
                      sizeMobile={35}
                      lineHeightMobile={45}
                      margin={[0]}
                    />
                  )}
                {checkIsApp() && (
                  <CustomText
                    text={product.productDescription}
                    textAlign="left"
                    onClickType={onClickTypeInText}
                    sizeMobile={24}
                    lineHeightMobile={32}
                    margin={[0]}
                  />
                )}
                {!checkIsApp() && (
                  <CustomText
                    text={product.productDescription}
                    textAlign="left"
                    onClickType={onClickTypeInText}
                    size={52}
                    lineHeight={70}
                    sizeMobile={35}
                    lineHeightMobile={45}
                    margin={[0]}
                  />
                )}
              </DescriptionContainer>
              {!product?.isLandingClub && productDetailButton()}
              {product?.isLandingClub && productLandingClubButton()}
              {checkIsApp() &&
                !product?.isLandingClub &&
                getProductElements() &&
                getProductElements()?.length > 0 && (
                  <ProductCharacteristics
                    elements={getProductElements()}
                    title={product?.offerLockInApp?.shortCharacteristicsLockIn?.title || ''}
                    promoDisclaimer={
                      product.isGlobalPromoActive ? globalpromo?.detailPageDisclaimer : undefined
                    }
                  />
                )}
              {disclaimer && (
                <DisclaimerContainer>
                  <CustomText
                    textAlign="left"
                    text={disclaimer}
                    lineHeight={22}
                    size={16}
                    fontFamily={fonts.regular}
                  />
                </DisclaimerContainer>
              )}
            </Fade>
          </ContainerTopProduct>
          {divider?.image && (
            <Fade direction="up" triggerOnce>
              <ImageDivider image={divider?.image} alt={divider.text || ''} />
            </Fade>
          )}
        </>
      )}
    </>
  );
};
export default TopNavigation;
